import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { Card, CardHeader, CardContent } from '../Common/Card';
import MainLayout from '../Layout/MainLayout';

const BankDebugLogs = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get run IDs and return info from URL
  const parentRunId = searchParams.get('parentRun');
  const childRunId = searchParams.get('childRun');
  const returnTo = searchParams.get('returnTo');
  const workflowId = searchParams.get('workflowId');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiCalls, setApiCalls] = useState([]);
  const [selectedCall, setSelectedCall] = useState(null);
  const [executionSummary, setExecutionSummary] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    request: false,
    response: true,
    headers: false,
    toolCalls: true,
    assistantResponse: true  // Auto-expand to show full AI reply
  });

  // Fetch logs on mount
  useEffect(() => {
    fetchLogs();
  }, [parentRunId, childRunId]);

  // Helper function to extract parameters from assistant message content
  const extractParametersFromContent = (content, toolName) => {
    if (!content) return null;

    const paramMatch = content.match(new RegExp(`Executing:\\s*${toolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\nParameters:\\s*\\n({[\\s\\S]*?})(?=\\n|$)`, 'i'));
    if (paramMatch && paramMatch[1]) {
      try {
        const cleanedParams = paramMatch[1]
          .replace(/\\n/g, '\n')
          .replace(/\\"/g, '"')
          .replace(/\\\\/g, '\\');
        return JSON.parse(cleanedParams);
      } catch (e) {
        console.warn('Failed to parse parameters from content:', e);
        return null;
      }
    }
    return null;
  };

  const transformV2Details = (data, workflowLabel = '') => {
    const transformedApiCalls = [];
    if (!data.execution_details) return transformedApiCalls;

    data.execution_details.forEach(step => {
      if (step.step_type === 'ui_step') {
        transformedApiCalls.push({
          id: step.step_execution_id,
          step_execution_id: step.step_execution_id,
          step_name: step.step_name,
          step_type: step.step_type,
          step_status: step.status,
          step_duration_ms: step.execution_time_ms,
          step_started_at: step.started_at,
          step_completed_at: step.completed_at,
          provider: null,
          model: null,
          request_url: null,
          request_body: step.input_data,
          response_body: step.output_data,
          response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
          duration_ms: step.execution_time_ms,
          tokens_used: step.tokens_used,
          cost: step.cost,
          request_timestamp: step.started_at,
          response_timestamp: step.completed_at,
          error_message: step.error_message,
          is_ui_step: true,
          workflow_label: workflowLabel
        });
      } else if (step.api_calls && step.api_calls.length > 0) {
        step.api_calls.forEach(call => {
          transformedApiCalls.push({
            id: call.id,
            step_execution_id: step.step_execution_id,
            step_name: step.step_name,
            step_type: step.step_type,
            step_status: step.status,
            step_duration_ms: step.execution_time_ms,
            step_started_at: step.started_at,
            step_completed_at: step.completed_at,
            provider: call.provider,
            model: call.model,
            request_url: call.request_url,
            request_body: call.request_body,
            request_headers: call.request_headers,
            response_body: call.response_body,
            response_headers: call.response_headers,
            response_status: call.response_status,
            duration_ms: call.duration_ms,
            tokens_used: call.tokens_used,
            cost: call.cost,
            request_timestamp: call.request_timestamp,
            response_timestamp: call.response_timestamp,
            error_message: step.error_message,
            is_ui_step: false,
            workflow_label: workflowLabel
          });
        });
      } else {
        transformedApiCalls.push({
          id: step.step_execution_id,
          step_execution_id: step.step_execution_id,
          step_name: step.step_name,
          step_type: step.step_type,
          step_status: step.status,
          step_duration_ms: step.execution_time_ms,
          step_started_at: step.started_at,
          step_completed_at: step.completed_at,
          provider: 'openai',
          model: 'gpt-4o (Assistant)',
          request_url: null,
          request_body: step.input_data,
          response_body: step.output_data,
          response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
          duration_ms: step.execution_time_ms,
          tokens_used: step.tokens_used,
          cost: step.cost,
          request_timestamp: step.started_at,
          response_timestamp: step.completed_at,
          error_message: step.error_message,
          is_ui_step: false,
          workflow_label: workflowLabel
        });
      }
    });
    return transformedApiCalls;
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('sankalvax_token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let allApiCalls = [];
      let totalSteps = 0;
      let totalApiCalls = 0;

      // Fetch parent workflow logs using bank-specific endpoint
      if (parentRunId) {
        try {
          const parentResponse = await fetch(`/api/bank/v2/workflows/executions/${parentRunId}/api-calls`, { headers });
          if (parentResponse.ok) {
            const data = await parentResponse.json();
            if (data.success && data.api_calls) {
              // Transform API calls to match expected format
              const parentCalls = data.api_calls.map(call => ({
                id: call.id,
                step_execution_id: call.step_execution_id,
                step_name: call.step_name || 'API Call',
                step_type: 'ai_step',
                step_status: call.response_status >= 200 && call.response_status < 300 ? 'completed' : 'failed',
                step_duration_ms: call.duration_ms,
                step_started_at: call.request_timestamp,
                step_completed_at: call.response_timestamp,
                provider: call.provider,
                model: call.model,
                request_url: call.request_url,
                request_body: call.request_body,
                request_headers: call.request_headers,
                response_body: call.response_body,
                response_headers: call.response_headers,
                response_status: call.response_status,
                duration_ms: call.duration_ms,
                tokens_used: call.tokens_used,
                cost: call.cost,
                request_timestamp: call.request_timestamp,
                response_timestamp: call.response_timestamp,
                error_message: call.error_message,
                is_ui_step: false,
                workflow_label: 'Parent - Bank Reconciliation'
              }));
              allApiCalls = [...allApiCalls, ...parentCalls];
              totalSteps += parentCalls.length;
              totalApiCalls += parentCalls.length;
            }
          }
        } catch (err) {
          console.warn('Failed to fetch parent logs:', err);
        }
      }

      // Fetch child workflow logs using bank-specific endpoint
      if (childRunId) {
        try {
          const childResponse = await fetch(`/api/bank/v2/workflows/executions/${childRunId}/api-calls`, { headers });
          if (childResponse.ok) {
            const data = await childResponse.json();
            if (data.success && data.api_calls) {
              // Transform API calls to match expected format
              const childCalls = data.api_calls.map(call => ({
                id: call.id,
                step_execution_id: call.step_execution_id,
                step_name: call.step_name || 'API Call',
                step_type: 'ai_step',
                step_status: call.response_status >= 200 && call.response_status < 300 ? 'completed' : 'failed',
                step_duration_ms: call.duration_ms,
                step_started_at: call.request_timestamp,
                step_completed_at: call.response_timestamp,
                provider: call.provider,
                model: call.model,
                request_url: call.request_url,
                request_body: call.request_body,
                request_headers: call.request_headers,
                response_body: call.response_body,
                response_headers: call.response_headers,
                response_status: call.response_status,
                duration_ms: call.duration_ms,
                tokens_used: call.tokens_used,
                cost: call.cost,
                request_timestamp: call.request_timestamp,
                response_timestamp: call.response_timestamp,
                error_message: call.error_message,
                is_ui_step: false,
                workflow_label: 'Child - Transaction Approval'
              }));
              allApiCalls = [...allApiCalls, ...childCalls];
              totalSteps += childCalls.length;
              totalApiCalls += childCalls.length;
            }
          }
        } catch (err) {
          console.warn('Failed to fetch child logs:', err);
        }
      }

      setApiCalls(allApiCalls);
      setExecutionSummary({
        total_steps: totalSteps,
        total_api_calls: totalApiCalls,
        parent_run_id: parentRunId,
        child_run_id: childRunId,
        isV2: true,
        isBank: true
      });

      if (allApiCalls.length > 0) {
        setSelectedCall(allApiCalls[0]);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // If opened from TransactionsModal, navigate back to BankChatHistory with openRun param
    // This will auto-open the TransactionsModal
    if (returnTo === 'transactions' && parentRunId) {
      navigate(`/bank-chat-history?openRun=${parentRunId}`);
    } else {
      navigate(-1);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    return `${ms}ms`;
  };

  const formatJson = (obj) => {
    try {
      if (typeof obj === 'string') {
        try {
          return JSON.stringify(JSON.parse(obj), null, 2);
        } catch {
          return obj;
        }
      }
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  // Extract the assistant's actual response content from response_body
  const getAssistantResponseContent = (call) => {
    if (!call || !call.response_body) return null;

    try {
      const responseBody = typeof call.response_body === 'string'
        ? JSON.parse(call.response_body)
        : call.response_body;

      // Check choices[0].message.content (OpenAI format)
      if (responseBody.choices && responseBody.choices[0]?.message?.content) {
        return responseBody.choices[0].message.content;
      }

      // Fallback: check direct content field
      if (responseBody.content) {
        return responseBody.content;
      }

      return null;
    } catch (e) {
      console.warn('Failed to extract assistant response:', e);
      return null;
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 400 && status < 500) return 'text-red-600';
    if (status >= 500) return 'text-red-800';
    return 'text-gray-600';
  };

  const getWorkflowBadgeColor = (label) => {
    if (label?.includes('Parent')) {
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    }
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  };

  // Extract tool calls from response - matching ApiLogsViewer.js
  const extractToolCalls = (call) => {
    const toolCalls = [];

    // Extract from request body (tool_calls sent to AI)
    if (call.request_body && call.request_body.messages) {
      const messages = call.request_body.messages;
      messages.forEach((message, msgIndex) => {
        if (message.role === 'assistant' && message.tool_calls) {
          message.tool_calls.forEach((toolCall) => {
            let parsedArgs = {};
            try {
              if (toolCall.function?.arguments) {
                parsedArgs = typeof toolCall.function.arguments === 'string'
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments;
              } else if (toolCall.arguments) {
                parsedArgs = toolCall.arguments;
              }

              // For legacy tool calls with empty arguments, try to extract from message content
              if (Object.keys(parsedArgs).length === 0) {
                const toolName = toolCall.function?.name || toolCall.name;
                const extractedParams = extractParametersFromContent(message.content, toolName);
                if (extractedParams) {
                  parsedArgs = extractedParams;
                } else {
                  parsedArgs = {
                    "__legacy_note": "Parameters not available in API logs",
                    "__tool_name": toolName,
                    "__hint": "Check chat messages for actual parameters used"
                  };
                }
              }
            } catch (e) {
              console.warn('Failed to parse tool arguments:', e);
              parsedArgs = { error: 'Failed to parse arguments', raw: toolCall.function?.arguments || toolCall.arguments };
            }

            toolCalls.push({
              type: 'outgoing',
              id: toolCall.id,
              name: toolCall.function?.name || toolCall.name,
              arguments: JSON.stringify(parsedArgs, null, 2),
              messageIndex: msgIndex,
              source: 'request'
            });
          });
        }

        // Tool results in messages
        if (message.role === 'tool') {
          toolCalls.push({
            type: 'result',
            id: message.tool_call_id,
            name: 'Tool Result',
            content: message.content,
            messageIndex: msgIndex,
            source: 'request'
          });
        }
      });
    }

    // Extract from response body (AI requesting tool calls)
    if (call.response_body && call.response_body.choices) {
      const message = call.response_body.choices[0]?.message;
      if (message && message.tool_calls) {
        message.tool_calls.forEach((toolCall) => {
          let parsedArgs = {};
          try {
            if (toolCall.function?.arguments) {
              parsedArgs = typeof toolCall.function.arguments === 'string'
                ? JSON.parse(toolCall.function.arguments)
                : toolCall.function.arguments;
            } else if (toolCall.arguments) {
              parsedArgs = toolCall.arguments;
            }

            if (Object.keys(parsedArgs).length === 0) {
              const toolName = toolCall.function?.name || toolCall.name;
              parsedArgs = {
                "__legacy_note": "Parameters not available in API logs",
                "__tool_name": toolName,
                "__hint": "Check chat messages for actual parameters used"
              };
            }
          } catch (e) {
            console.warn('Failed to parse tool arguments:', e);
            parsedArgs = { error: 'Failed to parse arguments', raw: toolCall.function?.arguments || toolCall.arguments };
          }

          toolCalls.push({
            type: 'requested',
            id: toolCall.id,
            name: toolCall.function?.name || toolCall.name,
            arguments: JSON.stringify(parsedArgs, null, 2),
            source: 'response'
          });
        });
      }
    }

    return toolCalls;
  };

  // Deduplicate tool calls - matching ApiLogsViewer.js
  const deduplicateToolCalls = (toolCalls) => {
    const groupedCalls = {};

    toolCalls.forEach((toolCall, index) => {
      const key = toolCall.type === 'result'
        ? `result_${toolCall.id}`
        : `call_${toolCall.name}`;

      if (!groupedCalls[key]) {
        groupedCalls[key] = [];
      }

      groupedCalls[key].push({ ...toolCall, originalIndex: index });
    });

    const deduplicatedCalls = [];

    Object.keys(groupedCalls).forEach(key => {
      const group = groupedCalls[key];

      if (group.length === 1) {
        deduplicatedCalls.push(group[0]);
      } else {
        if (key.startsWith('result_')) {
          group.forEach(call => {
            deduplicatedCalls.push(call);
          });
        } else {
          const callsWithParams = group.filter(call => {
            try {
              const args = JSON.parse(call.arguments || '{}');
              return Object.keys(args).length > 0 && !args.__legacy_note;
            } catch (e) {
              return false;
            }
          });

          const callsWithoutParams = group.filter(call => {
            try {
              const args = JSON.parse(call.arguments || '{}');
              return !(Object.keys(args).length > 0 && !args.__legacy_note);
            } catch (e) {
              return true;
            }
          });

          if (callsWithParams.length > 0) {
            const requestedCalls = callsWithParams.filter(call => call.type === 'requested');
            const selectedCall = requestedCalls.length > 0 ? requestedCalls[0] : callsWithParams[0];
            deduplicatedCalls.push(selectedCall);
          } else if (callsWithoutParams.length > 0) {
            const requestedCalls = callsWithoutParams.filter(call => call.type === 'requested');
            const selectedCall = requestedCalls.length > 0 ? requestedCalls[0] : callsWithoutParams[0];
            deduplicatedCalls.push(selectedCall);
          }
        }
      }
    });

    deduplicatedCalls.sort((a, b) => a.originalIndex - b.originalIndex);
    return deduplicatedCalls;
  };

  if (loading) {
    return (
      <MainLayout title="Bank Debug Logs">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading Bank Workflow Logs...</div>
        </div>
      </MainLayout>
    );
  }

  const renderExecutionSummary = () => {
    return (
      <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-green-50 dark:from-purple-900/20 dark:to-green-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
        <h3 className="text-lg font-semibold mb-3 text-purple-800 dark:text-purple-200">
          🏦 Bank Workflow Execution Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600 dark:text-gray-400">Total Steps:</span>
            <span className="ml-2 font-mono font-semibold text-purple-700 dark:text-purple-300">
              {executionSummary?.total_steps || 0}
            </span>
          </div>
          <div>
            <span className="text-gray-600 dark:text-gray-400">API Calls:</span>
            <span className="ml-2 font-mono font-semibold text-purple-700 dark:text-purple-300">
              {executionSummary?.total_api_calls || 0}
            </span>
          </div>
          {parentRunId && (
            <div>
              <span className="text-gray-600 dark:text-gray-400">Parent Run:</span>
              <span className="ml-2 font-mono text-xs bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded">
                {parentRunId?.substring(0, 8)}...
              </span>
            </div>
          )}
          {childRunId && (
            <div>
              <span className="text-gray-600 dark:text-gray-400">Child Run:</span>
              <span className="ml-2 font-mono text-xs bg-green-100 dark:bg-green-800 px-2 py-1 rounded">
                {childRunId?.substring(0, 8)}...
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <MainLayout title="Bank Debug Logs">
      <div className="p-6 space-y-6">
        {/* Controls */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Bank Workflow Debug</span>
            <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs rounded-full font-medium">
              Parent + Child
            </span>
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 text-xs rounded-full font-medium">
              V2 Engine
            </span>
          </div>
          <button
            onClick={handleBack}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
          >
            ← Back
          </button>
        </div>

        {/* Execution Summary */}
        {renderExecutionSummary()}

        {/* Error Display */}
        {error && (
          <Card>
            <CardContent>
              <div className="text-red-600 bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="font-medium">Error loading logs:</div>
                <div className="mt-1 text-sm">{error}</div>
              </div>
            </CardContent>
          </Card>
        )}

        {apiCalls.length === 0 ? (
          <Card>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                No execution logs found for this workflow
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Execution Steps List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <h3 className="font-medium">Execution Steps ({apiCalls.length})</h3>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[600px] overflow-y-auto">
                    {apiCalls.map((call, index) => (
                      <div
                        key={call.id || index}
                        onClick={() => setSelectedCall(call)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                          selectedCall?.id === call.id ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-blue-500' : ''
                        }`}
                      >
                        {/* Workflow Label Badge */}
                        {call.workflow_label && (
                          <div className="mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getWorkflowBadgeColor(call.workflow_label)}`}>
                              {call.workflow_label.includes('Parent') ? '🔷 Parent' : '🟢 Child'}
                            </span>
                          </div>
                        )}

                        <div className="flex justify-between items-start mb-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            #{index + 1} - {call.step_name}
                          </span>
                          <span className={`text-sm font-mono ${getStatusColor(call.response_status)}`}>
                            {call.response_status || 'N/A'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                          {call.is_ui_step ? (
                            <div className="flex items-center space-x-1">
                              <span className="text-blue-600 dark:text-blue-400">🖥️</span>
                              <span className="text-blue-600 dark:text-blue-400 font-medium">UI Step</span>
                            </div>
                          ) : (
                            <div>{call.provider} - {call.model}</div>
                          )}
                          <div>{formatDuration(call.duration_ms)}</div>
                          {!call.is_ui_step && call.tokens_used > 0 && (
                            <div>{call.tokens_used} tokens</div>
                          )}
                          {!call.is_ui_step && (() => {
                            const toolCallsCount = deduplicateToolCalls(extractToolCalls(call)).length;
                            return toolCallsCount > 0 ? (
                              <div className="flex items-center space-x-1">
                                <span className="text-purple-600 dark:text-purple-400">🔧</span>
                                <span className="text-purple-600 dark:text-purple-400 font-medium">
                                  {toolCallsCount} tool call{toolCallsCount !== 1 ? 's' : ''}
                                </span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                        {call.error_message && (
                          <div className="mt-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                            Error: {call.error_message.substring(0, 50)}...
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Selected Call Details */}
            <div className="lg:col-span-2">
              {selectedCall ? (
                <div className="space-y-4">
                  {/* Step Information */}
                  <Card>
                    <CardHeader>
                      <h3 className="font-medium text-blue-700 dark:text-blue-300">Step Information</h3>
                    </CardHeader>
                    <CardContent>
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Name:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono text-sm bg-white dark:bg-gray-800 px-2 py-1 rounded mt-1">
                              {selectedCall.step_name}
                            </div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Workflow:</label>
                            <div className={`inline-block px-2 py-1 rounded text-sm font-medium mt-1 ${getWorkflowBadgeColor(selectedCall.workflow_label)}`}>
                              {selectedCall.workflow_label || 'Unknown'}
                            </div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Type:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono">{selectedCall.step_type}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Status:</label>
                            <div className={`font-mono font-semibold ${
                              selectedCall.step_status === 'completed' ? 'text-green-600 dark:text-green-400' :
                              selectedCall.step_status === 'running' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-red-600 dark:text-red-400'
                            }`}>
                              {selectedCall.step_status}
                            </div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700 dark:text-gray-300">Duration:</label>
                            <div className="text-blue-800 dark:text-blue-200 font-mono">{formatDuration(selectedCall.step_duration_ms)}</div>
                          </div>
                          {selectedCall.step_started_at && (
                            <div>
                              <label className="font-medium text-gray-700 dark:text-gray-300">Timeline:</label>
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {formatTimestamp(selectedCall.step_started_at)}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* API Call Details */}
                  <Card>
                    <CardHeader>
                      <h3 className="font-medium">
                        {selectedCall.is_ui_step ? '🖥️ UI Step Details' : 'API Call Details'}
                      </h3>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        {!selectedCall.is_ui_step && (
                          <>
                            <div>
                              <label className="font-medium text-gray-700 dark:text-gray-300">Provider:</label>
                              <div className="text-gray-900 dark:text-gray-100">{selectedCall.provider}</div>
                            </div>
                            <div>
                              <label className="font-medium text-gray-700 dark:text-gray-300">Model:</label>
                              <div className="text-gray-900 dark:text-gray-100">{selectedCall.model}</div>
                            </div>
                          </>
                        )}
                        {selectedCall.is_ui_step && (
                          <div className="md:col-span-2">
                            <label className="font-medium text-gray-700 dark:text-gray-300">Step Type:</label>
                            <div className="text-blue-600 dark:text-blue-400 font-medium">
                              🖥️ User Interface Step
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="font-medium text-gray-700 dark:text-gray-300">Status:</label>
                          <div className={`font-mono ${getStatusColor(selectedCall.response_status)}`}>
                            {selectedCall.response_status || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <label className="font-medium text-gray-700 dark:text-gray-300">Duration:</label>
                          <div className="text-gray-900 dark:text-gray-100">{formatDuration(selectedCall.duration_ms)}</div>
                        </div>
                        {!selectedCall.is_ui_step && (
                          <>
                            <div>
                              <label className="font-medium text-gray-700 dark:text-gray-300">Tokens:</label>
                              <div className="text-gray-900 dark:text-gray-100">{selectedCall.tokens_used || 0}</div>
                            </div>
                            <div>
                              <label className="font-medium text-gray-700 dark:text-gray-300">Cost:</label>
                              <div className="text-gray-900 dark:text-gray-100">${(selectedCall.cost || 0).toFixed(6)}</div>
                            </div>
                          </>
                        )}
                      </div>
                      {!selectedCall.is_ui_step && selectedCall.request_url && (
                        <div className="mt-4">
                          <label className="font-medium text-gray-700 dark:text-gray-300">URL:</label>
                          <div className="text-gray-900 dark:text-gray-100 font-mono text-xs bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded mt-1 break-all">
                            {selectedCall.request_url}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tool Calls Section */}
                  {!selectedCall.is_ui_step && (() => {
                    const rawToolCalls = extractToolCalls(selectedCall);
                    const toolCalls = deduplicateToolCalls(rawToolCalls);
                    if (toolCalls.length === 0) return null;

                    return (
                      <Card>
                        <CardHeader>
                          <button
                            onClick={() => toggleSection('toolCalls')}
                            className="w-full text-left flex justify-between items-center"
                          >
                            <h3 className="font-medium text-purple-700 dark:text-purple-300">
                              🔧 Tool Calls ({toolCalls.length})
                            </h3>
                            <span>{expandedSections.toolCalls ? '▼' : '▶'}</span>
                          </button>
                        </CardHeader>
                        {expandedSections.toolCalls && (
                          <CardContent>
                            <div className="space-y-4">
                              {toolCalls.map((toolCall, index) => (
                                <div key={`${toolCall.id}-${index}`} className="border border-purple-200 dark:border-purple-700 rounded-lg p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-2">
                                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                        toolCall.type === 'requested'
                                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                          : toolCall.type === 'outgoing'
                                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      }`}>
                                        {toolCall.type === 'requested' ? '📤 AI Requested' :
                                         toolCall.type === 'outgoing' ? '🔄 Executing' : '✅ Result'}
                                      </span>
                                      <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                        🔧 {toolCall.name || 'Unknown Tool'}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                      ID: {toolCall.id?.substring(0, 12)}...
                                    </span>
                                  </div>

                                  {toolCall.arguments && (
                                    <div className="mb-4">
                                      <label className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-2 block">
                                        📥 Request Parameters:
                                      </label>
                                      <pre className="text-sm bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-700 overflow-x-auto font-mono max-h-48 overflow-y-auto">
                                        {typeof toolCall.arguments === 'string'
                                          ? toolCall.arguments
                                          : JSON.stringify(toolCall.arguments, null, 2)
                                        }
                                      </pre>
                                    </div>
                                  )}

                                  {toolCall.content && (
                                    <div>
                                      <label className="text-sm font-semibold mb-2 block text-green-700 dark:text-green-300">
                                        📤 Tool Response:
                                      </label>
                                      <pre className="text-sm p-4 rounded-lg border overflow-x-auto font-mono bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 max-h-48 overflow-y-auto">
                                        {typeof toolCall.content === 'string'
                                          ? toolCall.content
                                          : JSON.stringify(toolCall.content, null, 2)
                                        }
                                      </pre>
                                    </div>
                                  )}

                                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    Source: {toolCall.source} {toolCall.messageIndex !== undefined && `(message ${toolCall.messageIndex})`}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    );
                  })()}

                  {/* Assistant Response - Full AI Reply (only for AI steps with content) */}
                  {!selectedCall.is_ui_step && getAssistantResponseContent(selectedCall) && (
                    <Card className="border-2 border-blue-200 dark:border-blue-800">
                      <CardHeader className="bg-blue-50 dark:bg-blue-900/30">
                        <button
                          onClick={() => toggleSection('assistantResponse')}
                          className="w-full text-left flex justify-between items-center"
                        >
                          <h3 className="font-medium text-blue-700 dark:text-blue-300">
                            🤖 Assistant Response (Full AI Reply)
                          </h3>
                          <span>{expandedSections.assistantResponse ? '▼' : '▶'}</span>
                        </button>
                      </CardHeader>
                      {expandedSections.assistantResponse && (
                        <CardContent>
                          <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono whitespace-pre-wrap max-h-[600px] overflow-y-auto">
                            {(() => {
                              const content = getAssistantResponseContent(selectedCall);
                              // Try to parse and pretty-print if it's JSON
                              try {
                                const parsed = JSON.parse(content);
                                return JSON.stringify(parsed, null, 2);
                              } catch {
                                // Not JSON, return as-is
                                return content;
                              }
                            })()}
                          </pre>
                        </CardContent>
                      )}
                    </Card>
                  )}

                  {/* Request Body */}
                  <Card>
                    <CardHeader>
                      <button
                        onClick={() => toggleSection('request')}
                        className="w-full text-left flex justify-between items-center"
                      >
                        <h3 className="font-medium">
                          {selectedCall.is_ui_step ? '📥 Input Data' : 'Request Body'}
                        </h3>
                        <span>{expandedSections.request ? '▼' : '▶'}</span>
                      </button>
                    </CardHeader>
                    {expandedSections.request && (
                      <CardContent>
                        <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono max-h-96 overflow-y-auto">
                          {formatJson(selectedCall.request_body) || 'No request body'}
                        </pre>
                      </CardContent>
                    )}
                  </Card>

                  {/* Response Body */}
                  <Card>
                    <CardHeader>
                      <button
                        onClick={() => toggleSection('response')}
                        className="w-full text-left flex justify-between items-center"
                      >
                        <h3 className="font-medium">
                          {selectedCall.is_ui_step ? '📤 Output Data' : 'Response Body'}
                        </h3>
                        <span>{expandedSections.response ? '▼' : '▶'}</span>
                      </button>
                    </CardHeader>
                    {expandedSections.response && (
                      <CardContent>
                        {selectedCall.error_message ? (
                          <div className="text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded border border-red-200 dark:border-red-800 font-mono text-sm">
                            {selectedCall.error_message}
                          </div>
                        ) : (
                          <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-4 rounded overflow-x-auto border font-mono max-h-96 overflow-y-auto">
                            {formatJson(selectedCall.response_body) || 'No response body'}
                          </pre>
                        )}
                      </CardContent>
                    )}
                  </Card>

                  {/* Headers - Only for AI steps */}
                  {!selectedCall.is_ui_step && (
                    <Card>
                      <CardHeader>
                        <button
                          onClick={() => toggleSection('headers')}
                          className="w-full text-left flex justify-between items-center"
                        >
                          <h3 className="font-medium">Headers</h3>
                          <span>{expandedSections.headers ? '▼' : '▶'}</span>
                        </button>
                      </CardHeader>
                      {expandedSections.headers && (
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Request Headers:</h4>
                              <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded overflow-x-auto border font-mono">
                                {formatJson(selectedCall.request_headers) || 'No headers'}
                              </pre>
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Response Headers:</h4>
                              <pre className="text-xs bg-gray-50 dark:bg-gray-800 p-3 rounded overflow-x-auto border font-mono">
                                {formatJson(selectedCall.response_headers) || 'No headers'}
                              </pre>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  )}
                </div>
              ) : (
                <Card>
                  <CardContent>
                    <div className="text-center py-8 text-gray-500">
                      Select a step from the list to view details
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BankDebugLogs;
